/**
 * UK registration formatting (DVLA INF104). Pure: no DOM, used by the web plate, the API's
 * display strings and its emails.
 *
 *   current (Sept 2001+)  AB12 CDE   two letters, two-digit age identifier, three letters
 *   prefix  (1983–2001)   A123 BCD   age letter, 1–3 digits, three letters
 *   suffix  (1963–1983)   ABC 123D   three letters, 1–3 digits, age letter
 *   Northern Ireland      ABZ 1234   1–3 letters including I or Z, 1–4 digits
 *   dateless / personal   ABC 1, 1 ABC, AB 1234: letters and digits split into two groups
 *   anything else         unchanged (normalised, no space)
 */

export type RegFormat = 'current' | 'prefix' | 'suffix' | 'ni' | 'dateless' | 'unknown' | 'none';

/**
 * Values Aroo stores where a reg would go but that are not number plates: the old system's
 * pool marker (GUV = General Use Vehicle), "to be confirmed" and friends.
 */
const PLACEHOLDERS = new Set([
  '',
  'GUV',
  'TBC',
  'TBA',
  'TBD',
  'POOL',
  'NA',
  'NONE',
  'NIL',
  'UNKNOWN',
  'UNK',
  'NEW',
  'HIRE',
  'XXX',
  'X',
]);

/** Trim, upper-case and strip spaces, hyphens and other separators: " ab12-cde " → "AB12CDE". */
export function normaliseRegInput(input: string | null | undefined): string {
  return (input ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s\-–—._/\\]+/g, '');
}

/** True when the value is a placeholder (GUV, TBC, blank…) rather than a registration. */
export function isPlaceholderReg(input: string | null | undefined): boolean {
  const n = normaliseRegInput(input);
  return PLACEHOLDERS.has(n) || !/^[A-Z0-9]+$/.test(n);
}

const CURRENT = /^([A-Z]{2}\d{2})([A-Z]{3})$/;
const PREFIX = /^([A-Z]\d{1,3})([A-Z]{3})$/;
const SUFFIX = /^([A-Z]{3})(\d{1,3}[A-Z])$/;
const LETTERS_DIGITS = /^([A-Z]{1,3})(\d{1,4})$/;
const DIGITS_LETTERS = /^(\d{1,4})([A-Z]{1,3})$/;

interface Parsed {
  format: RegFormat;
  groups: string[];
}

function parse(input: string | null | undefined): Parsed {
  if (isPlaceholderReg(input)) return { format: 'none', groups: [] };
  const n = normaliseRegInput(input);
  let m = CURRENT.exec(n);
  if (m) return { format: 'current', groups: [m[1]!, m[2]!] };
  m = PREFIX.exec(n);
  if (m) return { format: 'prefix', groups: [m[1]!, m[2]!] };
  m = SUFFIX.exec(n);
  if (m) return { format: 'suffix', groups: [m[1]!, m[2]!] };
  m = LETTERS_DIGITS.exec(n);
  if (m) {
    // Northern Ireland marks always carry an I or a Z in the letters (e.g. "ABZ 1234", "IA 1").
    return { format: /[IZ]/.test(m[1]!) ? 'ni' : 'dateless', groups: [m[1]!, m[2]!] };
  }
  m = DIGITS_LETTERS.exec(n);
  if (m) return { format: 'dateless', groups: [m[1]!, m[2]!] };
  return { format: 'unknown', groups: [n] };
}

/** Which UK scheme a reg follows ("none" for placeholders, "unknown" for anything else). */
export function regFormatOf(input: string | null | undefined): RegFormat {
  return parse(input).format;
}

/** The reg as displayed on a plate, split into its DVLA groups: ["AB12", "CDE"]. */
export function regGroups(input: string | null | undefined): string[] {
  return parse(input).groups;
}

/**
 * Normalise and insert the DVLA spacing: "ab12cde" → "AB12 CDE", "k123abc" → "K123 ABC".
 * Unknown shapes come back normalised with no space; placeholders come back trimmed as given.
 */
export function formatReg(input: string | null | undefined): string {
  const p = parse(input);
  if (p.format === 'none') return (input ?? '').trim();
  return p.groups.join(' ');
}

/** True when the value looks like a UK registration in one of the known schemes. */
export function isLikelyUkReg(input: string | null | undefined): boolean {
  const f = regFormatOf(input);
  return f !== 'none' && f !== 'unknown';
}

export type RegTextPart = { text: string } | { reg: string };

/**
 * Regs written in free text, for sentences we can't restructure (stored notification titles,
 * email lines). Deliberately strict so ordinary words and refs don't light up: current marks
 * with or without the space, prefix/suffix marks with their space, and NI marks with theirs.
 */
const IN_TEXT =
  /\b(?:[A-Z]{2}\d{2} ?[A-Z]{3}|[A-Z]\d{1,3} [A-Z]{3}|[A-Z]{3} \d{1,3}[A-Z]|[A-Z]{0,2}[IZ] \d{1,4})\b/g;

/** Split text into plain runs and registrations: "Repair on AB12 CDE" → [text, reg]. */
export function splitRegsInText(text: string): RegTextPart[] {
  const parts: RegTextPart[] = [];
  let last = 0;
  for (const m of text.matchAll(IN_TEXT)) {
    const at = m.index;
    if (!isLikelyUkReg(m[0])) continue;
    if (at > last) parts.push({ text: text.slice(last, at) });
    parts.push({ reg: formatReg(m[0]) });
    last = at + m[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
}
